// Shared Section → Group → Leaf renderer.
//
// Walks the structured `items` array produced by buildBalanceSheet /
// buildIncomeStatement in lib/reports.js. Used by both the on-screen Report
// view (StaffDashboard / ClientPortal) and the printable view (PrintModal),
// so all statements (BS, BS PY, IS, IS PY, T12, CF) share one rendering
// pipeline.
//
// Item shapes:
//   { kind: 'leaf',  label, current, prior }
//   { kind: 'group', label, leaves: [{label, current, prior}, ...],
//                    subtotal, subtotal_prior }
//
// Indentation contract:
//   indent 0 = section header / section total
//   indent 1 = ungrouped leaves AND group headers / group subtotals
//   indent 2 = leaves inside a group

import React from 'react';

const formatCurrency = (v) => {
  if (v === undefined || v === null || Number.isNaN(v)) return '';
  const abs = Math.abs(v);
  const f = abs.toLocaleString('en-US', {
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  });
  return v < 0 ? `(${f})` : f;
};

// Render a list of structured items as table rows. Pass renderRow to plug in
// your own row component (BSRow / PLRow) — the helper handles indentation and
// group structure for you. Section header and section total are emitted by
// the caller, not by this helper.
export function StructuredItems({ items, RowComponent, showPrior, baseIndent = 1 }) {
  if (!items || items.length === 0) return null;
  return (
    <>
      {items.map((it, idx) => {
        if (it.kind === 'group') {
          return (
            <React.Fragment key={`g-${idx}-${it.label}`}>
              <RowComponent
                label={it.label}
                indent={baseIndent}
                isGroupHeader
                data-testid={`group-header-${slug(it.label)}`}
              />
              {it.leaves.map((l, li) => (
                <RowComponent
                  key={`l-${idx}-${li}`}
                  label={l.label}
                  value={l.current}
                  priorValue={showPrior ? l.prior : undefined}
                  indent={baseIndent + 1}
                  data-testid={`group-leaf-${slug(it.label)}-${slug(l.label)}`}
                />
              ))}
              <RowComponent
                label={`Total ${it.label}`}
                value={it.subtotal}
                priorValue={showPrior ? it.subtotal_prior : undefined}
                indent={baseIndent}
                isGroupSubtotal
                data-testid={`group-subtotal-${slug(it.label)}`}
              />
            </React.Fragment>
          );
        }
        return (
          <RowComponent
            key={`leaf-${idx}`}
            label={it.label}
            value={it.current}
            priorValue={showPrior ? it.prior : undefined}
            indent={baseIndent}
            data-testid={`leaf-${slug(it.label)}`}
          />
        );
      })}
    </>
  );
}

export { formatCurrency };

function slug(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}
